/**
 * Composant SendQuestionnaireDialog pour la page Satisfaction
 * Boîte de dialogue d'envoi rapide de questionnaire
 */

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export const SendQuestionnaireDialog = ({
  open,
  onOpenChange,
  types,
  destinataires,
  sendData,
  setSendData,
  onSend,
  sending
}) => {
  const handleTypeChange = (v) => {
    setSendData({
      ...sendData, 
      type: v, 
      destinataire_nom: '', 
      destinataire_email: '', 
      destinataire_id: ''
    });
  };

  const handleDestinataireChange = (v) => {
    const allDest = [
      ...(destinataires.stagiaires || []),
      ...(destinataires.entreprises || []),
      ...(destinataires.tuteurs || []),
      ...(destinataires.moniteurs || [])
    ];
    const selected = allDest.find(d => d.id === v);
    if (selected) {
      setSendData({
        ...sendData, 
        destinataire_id: v,
        destinataire_nom: selected.nom,
        destinataire_email: selected.email
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-questionnaire-dialog">
        <DialogHeader>
          <DialogTitle>Envoi rapide - Questionnaire</DialogTitle>
          <DialogDescription>
            Sélectionnez le type et le destinataire parmi les personnes de la plateforme
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label>Type de questionnaire</Label>
            <Select value={sendData.type} onValueChange={handleTypeChange}>
              <SelectTrigger data-testid="select-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(types).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
                <SelectItem value="financeur">Financeur (OPCO, Région, etc.)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sélection par nom pour les types liés à la plateforme */}
          {sendData.type !== 'financeur' && (
            <div>
              <Label>Sélectionner un destinataire</Label>
              <Select 
                value={sendData.destinataire_id || ''} 
                onValueChange={handleDestinataireChange}
              >
                <SelectTrigger data-testid="select-destinataire">
                  <SelectValue placeholder="Choisir une personne..." />
                </SelectTrigger>
                <SelectContent>
                  {sendData.type === 'stagiaire_diplome' && destinataires.stagiaires?.length > 0 && (
                    <>
                      {destinataires.stagiaires.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                    </>
                  )}
                  {sendData.type === 'entreprise' && destinataires.entreprises?.length > 0 && (
                    <>
                      {destinataires.entreprises.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                    </>
                  )}
                  {sendData.type === 'tuteur' && destinataires.tuteurs?.length > 0 && (
                    <>
                      {destinataires.tuteurs.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                    </>
                  )}
                  {/* Afficher tous si type générique */}
                  {!['stagiaire_diplome', 'entreprise', 'tuteur'].includes(sendData.type) && (
                    <>
                      {destinataires.stagiaires?.length > 0 && destinataires.stagiaires.map(d => (
                        <SelectItem key={`stag-${d.id}`} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                      {destinataires.entreprises?.length > 0 && destinataires.entreprises.map(d => (
                        <SelectItem key={`ent-${d.id}`} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                      {destinataires.tuteurs?.length > 0 && destinataires.tuteurs.map(d => (
                        <SelectItem key={`tut-${d.id}`} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                      {destinataires.moniteurs?.length > 0 && destinataires.moniteurs.map(d => (
                        <SelectItem key={`mon-${d.id}`} value={d.id}>{d.nom} ({d.email})</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Affichage du destinataire sélectionné */}
          {sendData.destinataire_nom && sendData.type !== 'financeur' && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-700">
                <strong>Destinataire :</strong> {sendData.destinataire_nom}
              </p>
              <p className="text-xs text-green-600">{sendData.destinataire_email}</p>
            </div>
          )}

          {/* Saisie manuelle pour financeur */}
          {sendData.type === 'financeur' && (
            <>
              <div>
                <Label>Nom de l'organisme</Label>
                <Input
                  value={sendData.destinataire_nom}
                  onChange={(e) => setSendData({...sendData, destinataire_nom: e.target.value})}
                  placeholder="Nom de l'organisme financeur"
                  data-testid="input-nom-financeur"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={sendData.destinataire_email}
                  onChange={(e) => setSendData({...sendData, destinataire_email: e.target.value})}
                  placeholder="email@exemple.com"
                  data-testid="input-email-financeur"
                />
              </div>
              <div>
                <Label>Adresse</Label>
                <Input
                  value={sendData.financeur_adresse || ''}
                  onChange={(e) => setSendData({...sendData, financeur_adresse: e.target.value})}
                  placeholder="Adresse complète..."
                  data-testid="input-adresse-financeur"
                />
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button 
            onClick={onSend} 
            disabled={sending || !sendData.destinataire_email} 
            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
            data-testid="btn-send-questionnaire"
          >
            {sending ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendQuestionnaireDialog;
